import {
  DEFAULT_THEME_PREFERENCE,
  isDarkReadyPath,
  ResolvedTheme,
  THEME_META_COLOR,
  THEME_STORAGE_KEY,
  ThemePreference,
} from 'constants/theme';

const PREFERENCES: ThemePreference[] = ['light', 'dark', 'system'];
const DARK_QUERY = '(prefers-color-scheme: dark)';
const REVEAL_MS = 420;

export function isThemePreference(value: unknown): value is ThemePreference {
  return PREFERENCES.includes(value as ThemePreference);
}

/** localStorage pode lançar (aba anônima, cookies bloqueados): cai no padrão. */
export function readStoredThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : DEFAULT_THEME_PREFERENCE;
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
}

export function storeThemePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Sem storage a escolha vale só nesta aba — não é erro.
  }
}

export function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

/** Assina mudanças do tema do sistema operacional. Devolve a função de cancelamento. */
export function watchSystemTheme(onChange: (prefersDark: boolean) => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => {};
  const query = window.matchMedia(DARK_QUERY);
  const listener = (event: MediaQueryListEvent) => onChange(event.matches);
  query.addEventListener('change', listener);
  return () => query.removeEventListener('change', listener);
}

export function resolveTheme(preference: ThemePreference, prefersDark: boolean): ResolvedTheme {
  if (preference === 'system') return prefersDark ? 'dark' : 'light';
  return preference;
}

/** Tema que a página de fato usa: o escuro só vale nas rotas já migradas. */
export function effectiveTheme(resolved: ResolvedTheme, pathname: string): ResolvedTheme {
  return resolved === 'dark' && isDarkReadyPath(pathname) ? 'dark' : 'light';
}

export function currentDocumentTheme(): ResolvedTheme {
  return document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light';
}

export function applyThemeToDocument(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.setAttribute('data-bs-theme', theme);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_META_COLOR[theme]);
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

type ViewTransitionDocument = Document & {
  startViewTransition?: (update: () => void) => { ready: Promise<void>; finished: Promise<void> };
};

/**
 * Troca o tema com animação: o novo tema é revelado num círculo que nasce de `origin`
 * (o centro do botão clicado). Sem View Transitions API, faz um fade curto; com
 * `prefers-reduced-motion`, troca na hora. `update` (ex.: dispatch da preferência) roda no
 * mesmo instante da troca de cores. Estilos em `assets/theme/_theme-switch.scss`.
 */
export function applyThemeWithTransition(
  theme: ResolvedTheme,
  origin?: { x: number; y: number },
  update: () => void = () => {},
): void {
  const swap = () => {
    applyThemeToDocument(theme);
    update();
  };
  if (currentDocumentTheme() === theme || prefersReducedMotion()) {
    swap();
    return;
  }

  const root = document.documentElement;
  const doc = document as ViewTransitionDocument;
  if (!doc.startViewTransition) {
    root.classList.add('ee-theme-fading');
    swap();
    window.setTimeout(() => root.classList.remove('ee-theme-fading'), 260);
    return;
  }

  const x = origin?.x ?? window.innerWidth / 2;
  const y = origin?.y ?? 0;
  const radius = Math.hypot(
    Math.max(x, window.innerWidth - x),
    Math.max(y, window.innerHeight - y),
  );

  root.classList.add('ee-theme-vt');
  // `update` roda DENTRO da transição: o navegador já fotografou o tema antigo, então o
  // estado do React (ex.: o segmentado ativo) muda junto com as cores, no mesmo quadro.
  const transition = doc.startViewTransition(swap);
  transition.ready.then(() => {
    root.animate(
      { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
      {
        duration: REVEAL_MS,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        pseudoElement: '::view-transition-new(root)',
      },
    );
  }).catch(() => {});
  transition.finished.finally(() => root.classList.remove('ee-theme-vt'));
}
